import { useEffect, useMemo, useState, useRef, FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";

// 修复：从 lucide-react 导入所有需要的图标
import {
  Search,
  MoreVertical,
  Edit,
  ArrowDownCircle,
  ArrowUpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { commodityService } from "@/services/commodity";
import {
  Commodity,
  CommodityStatus,
  CommodityUnit,
  GetCommoditiesParams,
  STATUS_OPTIONS,
  UNIT_OPTIONS,
} from "@/types/commodities";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
const formatPriceToYuan = (priceInCents: number | undefined) =>
  (priceInCents || 0) / 100;
const formatPriceToCents = (priceInYuan: number | string | undefined) =>
  Math.round(Number(priceInYuan || 0) * 100);

export default function CommoditiesManagement() {
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // 数据
  const [loading, setLoading] = useState(true);
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [total, setTotal] = useState(0);

  // 状态筛选
  const [status, setStatus] = useState<"all" |"active"|"inactive">("all");

  // 详情弹窗状态
  const [selectedCommodity, setSelectedCommodity] =
    useState<Partial<Commodity> | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // 表单内选择器数据控制
  const [statusInForm, setStatusInForm] = useState<
    CommodityStatus | undefined
  >();
  const [unitInForm, setUnitInForm] = useState<CommodityUnit | undefined>();

  const formRef = useRef<HTMLFormElement>(null);

  //分页器
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // UI状态 -> 接口参数映射
  const buildRequestParams = () => {
    const params: GetCommoditiesParams = {
      page,
      page_size: pageSize,
    };
    // 状态筛选
    if (status !== "all") {
      params.status = status;
    }
    return params;
  };

  const fetchCommodities = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res = await commodityService.getCommodities(params);
      const list = (res as {list?: Commodity[],total?: number}).list ?? [];
      const totalCount = (res as {list?: Commodity[],total?: number}).total ?? 0;
      setCommodities(list);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      toast.error("获取商品列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchCommodities();
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr || dateStr === "0001-01-01T00:00:00Z") return "-";
    return new Date(dateStr).toLocaleString();
  };

  const resetFilters = () => {
    setStatus("all");
    setPage(1);
    fetchCommodities();
  };

  // 查看商品详情
  const viewCommodityDetails = (commodity?: Commodity) => {
    if (commodity) {
      setSelectedCommodity({ ...commodity,price: formatPriceToYuan(commodity.price) });
      setStatusInForm(commodity.status);
      setUnitInForm(commodity.unit);
    } else {
      setSelectedCommodity({});
    }
    setDetailDialogOpen(true);
  };

  const onFormChange = (
    label:
      | "name"
      | "price"
      | "duration"
      | "unit"
      | "status"
      | "description"
      | "appleProductId",
    value: string
  ) => {
    if (label === "status") setStatusInForm(value as CommodityStatus);
    if (label === "unit") setUnitInForm(value as CommodityUnit);
    setSelectedCommodity((prev) => ({ ...prev, [label]: value }));
  };

  // 触发form提交校验
  const onSubmit = () => {
    formRef.current?.requestSubmit();
  };

  // 新增、更新商品信息
  const formSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedCommodity === null) return;
    
    const payload: Partial<Commodity> = {
      ...selectedCommodity,
      price: formatPriceToCents(selectedCommodity.price),
      duration: Number(selectedCommodity.duration || 0),
    };

    try {
      if (payload.id) {
        await commodityService.updateCommodityStatus(payload as Commodity);
        toast.success("商品更新成功");
      } else {
        await commodityService.createCommodity(payload as Commodity);
        toast.success("商品新增成功");
      }
      fetchCommodities();
      onDialogOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error("操作失败", {
        description:
          err instanceof Error ? err.message : "请检查输入或稍后重试",
      });
    }
  };

  // 新增：处理商品上架/下架的函数
  const handleUpdateStatus = async (
    commodity: Commodity,
    status: CommodityStatus
  ) => {
    const actionText = status === "active" ? "上架" : "下架";
    try {
      // 调用服务，传入商品完整信息和新的状态
      await commodityService.updateCommodityStatus({ ...commodity, status });
      toast.success(`商品「${commodity.name}」${actionText}成功`);
      // 刷新列表数据
      fetchCommodities();
    } catch (e) {
      console.error(e);
      toast.error(`商品${actionText}失败`, {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    }
  };

  const onDialogOpenChange = (open: boolean) => {
    if (!open) {
      setDetailDialogOpen(false);
      setStatusInForm(undefined);
      setUnitInForm(undefined);
      setSelectedCommodity(null);
    }
  };

  useEffect(() => {
    fetchCommodities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">商品管理</h2>
      </div>
      <div className="rounded-lg border p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">状态</label>
            <Select
              value={status}
              onValueChange={(val: "all" | CommodityStatus) => setStatus(val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="选择状态" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2 justify-end">
            <label className="text-sm text-gray-600 opacity-0">操作</label>
            <div className="flex gap-2 justify-end">
              <Button
                className="w-24"
                variant="outline"
                onClick={() => viewCommodityDetails()}
              >
                新增商品
              </Button>
              <Button className="w-24" variant="outline" onClick={resetFilters}>
                重置
              </Button>
              <Button className="w-24" onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                查询
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">商品名称</TableHead>
              <TableHead className="whitespace-nowrap">商品价格(元)</TableHead>
              <TableHead className="whitespace-nowrap">会员时长</TableHead>
              <TableHead className="whitespace-nowrap">时长单位</TableHead>
              <TableHead className="whitespace-nowrap">描述</TableHead>
              <TableHead className="whitespace-nowrap">苹果内购ID</TableHead>
              <TableHead className="whitespace-nowrap">创建日期</TableHead>
              <TableHead className="whitespace-nowrap">状态</TableHead>
              <TableHead className="whitespace-nowrap">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : commodities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="h-24 text-center">
                  没有找到商品记录
                </TableCell>
              </TableRow>
            ) : (
              commodities.map((commodity) => (
                <TableRow key={commodity.id}>
                  <TableCell className="max-w-[150px] truncate">
                    {commodity.name}
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate">
                    {formatPriceToYuan(commodity.price)}
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate">
                    {commodity.duration}
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate">
                    {commodity.unit === "year" ? "年" : "月"}
                  </TableCell>
                  <TableCell
                    className="max-w-[150px] truncate"
                    title={commodity.description}
                  >
                    {commodity.description}
                  </TableCell>
                  <TableCell className="max-w-[150px] truncate">
                    {commodity.appleProductId || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDateTime(commodity.createdAt || "-:-")}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        commodity.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {commodity.status === "active" ? "使用中" : "下架"}
                    </span>
                  </TableCell>
                  {/* 更新：实现完整的操作菜单 */}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-muted/60 rounded-full"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" sideOffset={4}>
                        <DropdownMenuLabel>相关操作</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => viewCommodityDetails(commodity)}
                          className="cursor-pointer"
                        >
                          <Edit className="mr-2 h-4 w-4 text-blue-600" />
                          编辑详情
                        </DropdownMenuItem>

                        {commodity.status === "active" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                handleUpdateStatus(commodity, "inactive")
                              }
                              className="cursor-pointer text-red-600 focus:text-red-700"
                            >
                              <ArrowDownCircle className="mr-2 h-4 w-4" />
                              下架商品
                            </DropdownMenuItem>
                          </>
                        )}

                        {commodity.status === "inactive" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() =>
                                handleUpdateStatus(commodity, "active")
                              }
                              className="cursor-pointer text-green-600 focus:text-green-700"
                            >
                              <ArrowUpCircle className="mr-2 h-4 w-4" />
                              上架商品
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selectedCommodity && (
        <Dialog open={detailDialogOpen} onOpenChange={onDialogOpenChange}>
          <DialogContent className="sm:max-w-xl bg-white">
            <DialogHeader>
              <DialogTitle className="text-center">
                {selectedCommodity.id ? "商品详情" : "新增商品"}
              </DialogTitle>
            </DialogHeader>
            <form ref={formRef} onSubmit={formSubmit}>
              <div className="space-y-2 grid grid-cols-6">
                <Label
                  htmlFor="name"
                  className="flex items-center justify-left mt-2"
                >
                  商品名称<div className="text-red-600">*</div>
                </Label>
                <Input
                  id="name"
                  defaultValue={selectedCommodity.name}
                  className="col-span-5"
                  required
                  autoComplete="off"
                  onChange={(e) => onFormChange("name", e.target.value)}
                />
              </div>
              <div className="space-y-2 grid grid-cols-6">
                <Label
                  htmlFor="price"
                  className="flex items-center justify-left mt-2"
                >
                  商品价格(元)<div className="text-red-600">*</div>
                </Label>
                <Input
                  id="price"
                  type="number"
                  defaultValue={selectedCommodity.price}
                  className="col-span-5"
                  required
                  autoComplete="off"
                  placeholder="存储的价格最多精确到分"
                  step="0.01"
                  onChange={(e) => onFormChange("price", e.target.value)}
                />
              </div>
              <div className="space-y-2 grid grid-cols-6">
                <Label
                  htmlFor="duration"
                  className="flex items-center justify-left mt-2"
                >
                  会员时长<div className="text-red-600">*</div>
                </Label>
                <Input
                  id="duration"
                  type="number"
                  defaultValue={selectedCommodity.duration}
                  className="col-span-5"
                  required
                  autoComplete="off"
                  onChange={(e) => onFormChange("duration", e.target.value)}
                />
              </div>
              <div className="space-y-2 grid grid-cols-6">
                <Label
                  htmlFor="unit"
                  className="flex items-center justify-left mt-2"
                >
                  时长单位<div className="text-red-600">*</div>
                </Label>
                <div className="col-span-5">
                  <Select
                    value={unitInForm}
                    onValueChange={(val: string) => onFormChange("unit", val)}
                    required
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue id="unit" placeholder="选择单位" />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2 grid grid-cols-6">
                <Label
                  htmlFor="status"
                  className="flex items-center justify-left mt-2"
                >
                  状态
                </Label>
                <div className="col-span-5">
                  <Select
                    value={statusInForm}
                    onValueChange={(val: string) => onFormChange("status", val)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue id="status" placeholder="选择状态" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.slice(1).map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2 grid grid-cols-6">
                <Label
                  htmlFor="appleProductId"
                  className="flex items-center justify-left mt-2"
                >
                  苹果内购ID
                </Label>
                <Input
                  id="appleProductId"
                  defaultValue={selectedCommodity.appleProductId}
                  className="col-span-5"
                  onChange={(e) =>
                    onFormChange("appleProductId", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2 grid grid-cols-6">
                <Label
                  htmlFor="description"
                  className="flex items-center justify-left mt-2"
                >
                  商品描述
                </Label>
                <Textarea
                  id="description"
                  defaultValue={selectedCommodity.description}
                  className="col-span-5"
                  onChange={(e) => onFormChange("description", e.target.value)}
                />
              </div>
            </form>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">取消</Button>
              </DialogClose>
              <Button onClick={onSubmit}>确认</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {!loading && commodities.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 条记录，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => page > 1 && setPage(page - 1)}
                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => page < totalPages && setPage(page + 1)}
                  className={
                    page >= totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
